import { useAdministrationArea } from "../providers/administrationAreaProvider";
import { useEasySession } from "../providers/easySessionProvider";
import { useAtomValue } from "jotai";

import { externalCredentialsHandoffAtom } from "./externalCredentialsHandoff";
import { useEnsureQuoteExists } from "./useEnsureQuoteExists";

/**
 * Derives the single admin-area/onboarding status consumed by `OnboardingGate`
 *
 * - "checking": check session, admin-area, quote,
 *   or external-credentials from an URL.
 *
 * - "needs-administration-area": ahow the AdministrationAreaDialog if
 *   the Administration Area returned by the backend is empty.
 *
 * - "fetch-error": admin-area or quote request failed.
 *
 * - "ready": admin area and optional easy session are set up.
 */
export type OnboardingStatus =
  "checking" | "needs-administration-area" | "fetch-error" | "ready";

export function useOnboardingStatus(): OnboardingStatus {
  const { isEasySessionInit } = useEasySession();
  const isExternalCredentialsHandoffInProgress = useAtomValue(
    externalCredentialsHandoffAtom,
  );
  const { isReady: isQuoteReady, isError: isQuoteError } =
    useEnsureQuoteExists();
  const {
    administrationArea,
    isSuccessGetAdministrationAreaData,
    isErrorGetAdministrationAreaData,
  } = useAdministrationArea();

  if (isEasySessionInit || isExternalCredentialsHandoffInProgress) {
    return "checking";
  }
  if (isErrorGetAdministrationAreaData || isQuoteError) {
    return "fetch-error";
  }
  if (!isSuccessGetAdministrationAreaData) {
    return "checking";
  }
  if (!administrationArea) {
    if (!isQuoteReady) {
      return "checking";
    }
    return "needs-administration-area";
  }
  return "ready";
}
